var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Observable } from '../../Observable.js';
import { Stack } from '../../Stack.js';
import { IMatrix4, CameraTransitionPreset, TransitionState, IVector3 } from '../../api.js';
import { matrix_pool, radians, vector4_pool } from '../../Math.js';
import { Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { smooth_step } from '../../Interpolation.js';
import { update_from_partial } from '../utility/utils.js';
import { assert } from '../utility/Assert.js';
import { aabb_corners } from '../utility/Bounds.js';
import { LerpTargetPositionCameraTransition as LerpPositionTargetCameraTransition } from './CameraTransition.js';
import { Logger } from '../../logging/Logger.js';
import { Debug } from '../../utils/DebugLayer/utils/DebugDecorators.js';
const ENGINE_CAMERA_DIRTY_ANGLE_EPSILON = radians(0.05);
const ENGINE_CAMERA_DIRTY_POSITION_EPSILON = 0.00005;
// This is the default transition used by the viewer
export const SMOOTH_CAMERA_TRANSITION = {
    interpolation: smooth_step,
    duration_seconds: 2.0
};
Object.freeze(SMOOTH_CAMERA_TRANSITION);
export const SMOOTH_FAST_CAMERA_TRANSITION = {
    interpolation: smooth_step,
    duration_seconds: 0.3
};
Object.freeze(SMOOTH_FAST_CAMERA_TRANSITION);
export const LINEAR_CAMERA_TRANSITION = {
    interpolation: (t) => t,
    duration_seconds: 2.0
};
Object.freeze(LINEAR_CAMERA_TRANSITION);
export const LINEAR_FAST_CAMERA_TRANSITION = {
    interpolation: (t) => t,
    duration_seconds: 0.3
};
Object.freeze(LINEAR_FAST_CAMERA_TRANSITION);
export const TRANSITION_PRESETS = {
    [CameraTransitionPreset.SMOOTH]: SMOOTH_CAMERA_TRANSITION,
    [CameraTransitionPreset.SMOOTH_FAST]: SMOOTH_FAST_CAMERA_TRANSITION,
    [CameraTransitionPreset.LINEAR]: LINEAR_CAMERA_TRANSITION,
    [CameraTransitionPreset.LINEAR_FAST]: LINEAR_FAST_CAMERA_TRANSITION
};
/**
 * Class that is responsible for managing which camera is the active camera.
 * The active camera is the camera object that updated the rendering ViewerCamera.
 * It's also responsible for interpolating between cameras during transitions.
 */
let CameraManager = class CameraManager {
    constructor(engine_camera, _interaction_system, _viewer, initial_aspect_ratio) {
        this._interaction_system = _interaction_system;
        this._viewer = _viewer;
        this._camera_stack = new Stack();
        this._camera_dispose_observer = null;
        this._camera_transition = null;
        this._interaction_begin_observer = null;
        this._interaction_end_observer = null;
        this.active_camera_changed_observable = new Observable();
        this.camera_transition_begin_observable = new Observable();
        this.camera_transition_end_observable = new Observable();
        this._interaction_id = null;
        this._engine_camera = engine_camera;
        this._last_aspect_ratio = initial_aspect_ratio;
        this._camera_dispose_callback = () => {
            this.pop_camera_immediate();
        };
        this._interaction_begin_callback = () => {
            if (this._interaction_id) {
                // We already have a running interaction, lets keep that ID, nothing to do
            }
            else {
                this._interaction_id = this._interaction_system.begin_interaction();
            }
        };
        this._interaction_end_callback = () => {
            if (this._interaction_id) {
                this._interaction_system.end_interaction(this._interaction_id);
                this._interaction_id = null;
            }
            else {
                // We didn't have a running interaction, nothing to do.
            }
        };
    }
    set_engine_camera_view_matrix(matrix) {
        const current_inverse_view_matrix = this.engine_camera.get_view_matrix();
        IMatrix4.orthonormal_rotation_affine_transform_inverse_to_ref(current_inverse_view_matrix, current_inverse_view_matrix);
        const inverse_matrix = IMatrix4.orthonormal_rotation_affine_transform_inverse(matrix);
        if (IVector3.dot(inverse_matrix[0], current_inverse_view_matrix[0]) > Math.cos(ENGINE_CAMERA_DIRTY_ANGLE_EPSILON) &&
            IVector3.dot(inverse_matrix[1], current_inverse_view_matrix[1]) > Math.cos(ENGINE_CAMERA_DIRTY_ANGLE_EPSILON) &&
            IVector3.dot(inverse_matrix[2], current_inverse_view_matrix[2]) > Math.cos(ENGINE_CAMERA_DIRTY_ANGLE_EPSILON) &&
            Math.abs(inverse_matrix[3].x - current_inverse_view_matrix[3].x) < ENGINE_CAMERA_DIRTY_POSITION_EPSILON &&
            Math.abs(inverse_matrix[3].y - current_inverse_view_matrix[3].y) < ENGINE_CAMERA_DIRTY_POSITION_EPSILON &&
            Math.abs(inverse_matrix[3].z - current_inverse_view_matrix[3].z) < ENGINE_CAMERA_DIRTY_POSITION_EPSILON) {
            return;
        }
        this.engine_camera.set_view_matrix(matrix);
    }
    get active_camera() {
        return this._camera_stack.peek();
    }
    // Internal function to the Viewer
    get engine_camera() {
        return this._engine_camera;
    }
    is_transitioning() {
        return (!!this._camera_transition && !this._camera_transition.has_ended);
    }
    dispose() {
        // Does nothing. Required for Debug.Persistent()
    }
    update(delta_time) {
        var _a;
        const active_camera = this._camera_stack.peek();
        if (active_camera) {
            active_camera.update(delta_time);
            // The transition will update the engine camera while it's running.
            if (this.is_transitioning()) {
                // TODO: How should we handle aspect ratio? it's kinda hacky that we ask the camera about it.
                // We also talk about removing the storing of aspect from cameras making it a required param for functions that need it
                const state = (_a = this._camera_transition) === null || _a === void 0 ? void 0 : _a.step(delta_time, active_camera.projection.get_aspect_ratio());
                if (state === TransitionState.STARTED) {
                    this.camera_transition_begin_observable.notify(this, {
                        from_camera: null,
                        to_camera: this._camera_transition.to_camera,
                        canceled: false
                    });
                }
                else if (state === TransitionState.COMPLETED) {
                    this.camera_transition_end_observable.notify(this, {
                        from_camera: null,
                        to_camera: this._camera_transition.to_camera,
                        canceled: false
                    });
                    this._camera_transition = null;
                }
                else if (state === TransitionState.CONTINUE) {
                    // Nothing to do here, transition is still running
                }
                else {
                    Logger.as('CameraManager').error('Camera transition: Stepping transition returned a bad state. Abort');
                    this.camera_transition_end_observable.notify(this, {
                        from_camera: null,
                        to_camera: this._camera_transition.to_camera,
                        canceled: true
                    });
                    this._camera_transition = null;
                }
            }
            else {
                this._engine_camera.camera.mode = active_camera.projection.get_camera_mode();
                this._engine_camera.set_projection_matrix(active_camera.projection.get_projection_matrix(), active_camera.projection.get_camera_mode());
                this.set_engine_camera_view_matrix(active_camera.view_matrix);
            }
        }
    }
    unregister_callbacks_from_camera(viewer_camera) {
        if (this._camera_dispose_observer !== null) {
            viewer_camera.dispose_observable.remove(this._camera_dispose_observer);
        }
        if (this._interaction_begin_observer !== null) {
            viewer_camera.interaction_begin_observable.remove(this._interaction_begin_observer);
            this._interaction_begin_observer = null;
        }
        if (this._interaction_end_observer !== null) {
            viewer_camera.interaction_end_observable.remove(this._interaction_end_observer);
            this._interaction_end_observer = null;
        }
    }
    setup_new_active_camera(viewer_camera) {
        this._camera_dispose_observer = viewer_camera.dispose_observable.add(this._camera_dispose_callback);
        this._interaction_begin_observer = viewer_camera.interaction_begin_observable.add(this._interaction_begin_callback);
        this._interaction_end_observer = viewer_camera.interaction_end_observable.add(this._interaction_end_callback);
        this.active_camera_changed_observable.notify(this, viewer_camera);
    }
    /**
     * This contains logic for either running a transition or performing an immediate transition if
     * we are missing a camera to transition from.
     * This function also cancels previous transitions if already running
     */
    start_camera_transition(factory, old_camera, new_camera, caller_name) {
        if (this._camera_transition && this._camera_transition.has_started && !this._camera_transition.has_ended) {
            this.camera_transition_end_observable.notify(this, {
                from_camera: null,
                to_camera: this._camera_transition.to_camera,
                canceled: true
            });
            old_camera = this._camera_transition.transition_camera;
        }
        if (old_camera !== null) {
            this._camera_transition = factory(old_camera, new_camera, this.engine_camera);
        }
        else {
            Logger.as('CameraManager').info(`${caller_name} was called with a transition but there was no camera to transition from. Performing immediate transition instead`);
            this._camera_transition = null;
            this.set_engine_camera_view_matrix(new_camera.view_matrix);
            this._engine_camera.set_projection_matrix(new_camera.get_projection_matrix(), new_camera.projection.get_camera_mode());
            this.camera_transition_begin_observable.notify(this, {
                canceled: true,
                to_camera: new_camera,
                from_camera: null
            });
            this.camera_transition_end_observable.notify(this, {
                canceled: true,
                to_camera: new_camera,
                from_camera: null
            });
        }
    }
    push_camera(viewer_camera, transition) {
        const _transition = update_from_partial(SMOOTH_CAMERA_TRANSITION, transition !== null && transition !== void 0 ? transition : {});
        this.push_camera_custom_transition(viewer_camera, (from_camera, to_camera, engine_camera) => {
            return new LerpPositionTargetCameraTransition(from_camera, to_camera, engine_camera, _transition.duration_seconds, _transition.interpolation);
        });
    }
    push_camera_immediate(viewer_camera) {
        // Make sure the camera is added with the correct aspect ratio
        viewer_camera.update_aspect_ratio(this._last_aspect_ratio);
        const prev_camera = this._camera_stack.peek();
        if (prev_camera) {
            this.unregister_callbacks_from_camera(prev_camera);
        }
        this._camera_stack.push(viewer_camera);
        this.setup_new_active_camera(viewer_camera);
        this._camera_transition = null;
        this.set_engine_camera_view_matrix(viewer_camera.view_matrix);
        this._engine_camera.set_projection_matrix(viewer_camera.get_projection_matrix(), viewer_camera.projection.get_camera_mode());
        viewer_camera.on_activation();
    }
    push_camera_custom_transition(viewer_camera, factory) {
        // Make sure the camera is added with the correct aspect ratio
        viewer_camera.update_aspect_ratio(this._last_aspect_ratio);
        const prev_camera = this._camera_stack.peek();
        if (prev_camera !== null) {
            this.unregister_callbacks_from_camera(prev_camera);
        }
        this._camera_stack.push(viewer_camera);
        this.setup_new_active_camera(viewer_camera);
        this.start_camera_transition(factory, prev_camera, viewer_camera, 'push_camera');
        viewer_camera.on_activation();
    }
    /**
     * When popping cameras, the stack can contain disposed entities.
     * This function makes sure that the camera on top of the stack is either alive
     * or the stack is empty
     */
    pop_to_living_camera() {
        var _a;
        do {
            const prev_camera = this._camera_stack.pop();
            if (prev_camera) {
                this.unregister_callbacks_from_camera(prev_camera);
            }
            const new_camera = this._camera_stack.peek();
            if (new_camera === null) {
                console.warn('Camera stack became empty, abort');
                return;
            }
            else if (new_camera.disposed) {
                // We poped to a disposed camera, continue loop
                continue;
            }
            this.setup_new_active_camera(new_camera);
            new_camera.on_deactivation();
        } while ((_a = this._camera_stack.peek()) === null || _a === void 0 ? void 0 : _a.disposed); // Pop camera until the camera is not disposed
    }
    pop_camera(transition) {
        const _transition = update_from_partial(SMOOTH_CAMERA_TRANSITION, transition !== null && transition !== void 0 ? transition : {});
        this.pop_camera_custom_transition((from_camera, to_camera, engine_camera) => {
            return new LerpPositionTargetCameraTransition(from_camera, to_camera, engine_camera, _transition.duration_seconds, _transition.interpolation);
        });
    }
    pop_camera_immediate() {
        this.pop_to_living_camera();
        const new_camera = this._camera_stack.peek();
        this._camera_transition = null;
        if (new_camera) {
            this.set_engine_camera_view_matrix(new_camera.view_matrix);
            this._engine_camera.set_projection_matrix(new_camera.get_projection_matrix(), new_camera.projection.get_camera_mode());
        }
    }
    pop_camera_custom_transition(factory) {
        const prev_camera = this._camera_stack.peek();
        assert(prev_camera !== null, 'Pop camera cant be performed on an empty camera stack');
        this.pop_to_living_camera();
        const new_camera = this._camera_stack.peek();
        if (new_camera === null)
            return; // Here we early out since we cant perform a transition when the stack became empty
        let old_camera = prev_camera;
        if (this._camera_transition && this._camera_transition.has_started && !this._camera_transition.has_ended) {
            this.camera_transition_end_observable.notify(this, {
                from_camera: null,
                to_camera: this._camera_transition.to_camera,
                canceled: true
            });
            old_camera = this._camera_transition.transition_camera;
        }
        this._camera_transition = factory(old_camera, new_camera, this.engine_camera);
        new_camera.on_activation();
    }
    replace_camera(viewer_camera, transition) {
        const _transition = update_from_partial(SMOOTH_CAMERA_TRANSITION, transition !== null && transition !== void 0 ? transition : {});
        this.replace_camera_custom_transition(viewer_camera, (from_camera, to_camera, engine_camera) => {
            return new LerpPositionTargetCameraTransition(from_camera, to_camera, engine_camera, _transition.duration_seconds, _transition.interpolation);
        });
    }
    replace_camera_immediate(viewer_camera) {
        const poped_camera = this._camera_stack.pop();
        if (poped_camera) {
            this.unregister_callbacks_from_camera(poped_camera);
            poped_camera.on_deactivation();
        }
        // Add camera to the camera stack
        this._camera_stack.push(viewer_camera);
        this.setup_new_active_camera(viewer_camera);
        this._camera_transition = null;
        this.set_engine_camera_view_matrix(viewer_camera.view_matrix);
        this._engine_camera.set_projection_matrix(viewer_camera.get_projection_matrix(), viewer_camera.projection.get_camera_mode());
        viewer_camera.on_activation();
    }
    replace_camera_custom_transition(viewer_camera, factory) {
        const prev_camera = this._camera_stack.pop();
        assert(prev_camera !== null, 'replace camera cant be performed on an empty camera stack, use immediate variant');
        if (prev_camera) {
            this.unregister_callbacks_from_camera(prev_camera);
        }
        // Add camera to the camera stack
        this._camera_stack.push(viewer_camera);
        this.setup_new_active_camera(viewer_camera);
        this.start_camera_transition(factory, prev_camera, viewer_camera, 'replace_camera');
        viewer_camera.on_activation();
    }
    set_camera_stack_internal(viewer_cameras) {
        const previous_camera = this.active_camera;
        if (previous_camera) {
            this.unregister_callbacks_from_camera(previous_camera);
        }
        this._camera_stack.clear();
        for (const camera of viewer_cameras) {
            this._camera_stack.push(camera);
        }
        this.setup_new_active_camera(this.active_camera);
    }
    set_camera_stack(viewer_cameras, transition) {
        const _transition = update_from_partial(SMOOTH_CAMERA_TRANSITION, transition !== null && transition !== void 0 ? transition : {});
        this.set_camera_stack_custom_transition(viewer_cameras, (from_camera, to_camera, engine_camera) => {
            return new LerpPositionTargetCameraTransition(from_camera, to_camera, engine_camera, _transition.duration_seconds, _transition.interpolation);
        });
    }
    set_camera_stack_immediate(viewer_cameras) {
        assert(viewer_cameras.length > 0, 'set_camera_stack can not set to an empty array');
        this.set_camera_stack_internal(viewer_cameras);
        assert(this.active_camera, 'No valid active camera');
        this._camera_transition = null;
        this.set_engine_camera_view_matrix(this.active_camera.view_matrix);
        this._engine_camera.set_projection_matrix(this.active_camera.get_projection_matrix(), this.active_camera.projection.get_camera_mode());
    }
    set_camera_stack_custom_transition(viewer_cameras, factory) {
        assert(viewer_cameras.length > 0, 'set_camera_stack can not set to an empty array');
        const prev_camera = this.active_camera;
        assert(prev_camera !== null, 'set camera stack cant be performed on an empty camera stack, use immediate variant');
        this.set_camera_stack_internal(viewer_cameras);
        assert(this.active_camera, 'No valid active camera');
        this.start_camera_transition(factory, prev_camera, this.active_camera, 'set_camera_stack');
        this.active_camera.on_activation();
    }
    static get_frustum_vertices_world(camera) {
        const vertices = [];
        if (camera === null)
            return vertices;
        const view_matrix = matrix_pool.fetch().fromIMatrix4(camera.view_matrix);
        const projection_matrix = matrix_pool.fetch().fromIMatrix4(camera.get_projection_matrix());
        const inverse_view_projection_matrix = view_matrix.multiplyToRef(projection_matrix, matrix_pool.fetch());
        inverse_view_projection_matrix.invert();
        const projected = vector4_pool.fetch();
        for (let i = 0; i < aabb_corners.length; i++) {
            BABYLON_Vector4.TransformCoordinatesFromFloatsToRef(aabb_corners[i].x * 2.0 - 1.0, aabb_corners[i].y * 2.0 - 1.0, aabb_corners[i].z * 2.0 - 1.0, inverse_view_projection_matrix, projected);
            projected.scaleInPlace(1.0 / projected.w);
            vertices.push(new BABYLON_Vector3(projected.x, projected.y, projected.z));
        }
        vector4_pool.return(projected);
        matrix_pool.return(inverse_view_projection_matrix, projection_matrix, view_matrix);
        return vertices;
    }
    /**
     * Update active camera's aspect ratio.
     * Since a camera can technically belong to multiple camera managers, this needs to be done as soon as the camera's are used.
     * It should be a light operation.
     * @param aspect_ratio
     */
    update_aspect_ratio(aspect_ratio) {
        var _a;
        (_a = this.active_camera) === null || _a === void 0 ? void 0 : _a.update_aspect_ratio(aspect_ratio);
        this._last_aspect_ratio = aspect_ratio;
    }
};
CameraManager = __decorate([
    Debug.Persistent({
        name: "CameraManager",
        is_enabled: false
    })
], CameraManager);
export { CameraManager };
//# sourceMappingURL=CameraManager.js.map